import { Component } from "@theme/component";
import { debounce } from "@theme/utilities";

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @property {HTMLButtonElement} searchButton - The search button element.
 * @property {HTMLButtonElement} resetButton - The reset button element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ["searchPageInput"];
  optionalRefs = ["searchButton", "resetButton"];

  connectedCallback() {
    super.connectedCallback();
    this.#updateButtonVisibility();
  }

  /**
   * Handles the input event on the search input and toggles button visibility.
   */
  handleInput() {
    this.#updateButtonVisibility();
  }

  /**
   * Handles the click event on the clear button and submits an empty search.
   * This clears the search input and resubmits the form if the page is not
   * already in an empty state.
   */
  handleClearClick() {
    this.#submitEmptySearch();
    this.#updateButtonVisibility();
  }

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === "Escape" && value === "") {
      this.#submitEmptySearch();
    }
  }, 100);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = "";

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get("q") ?? "";

    return queryParam.trim() === "";
  };

  #updateButtonVisibility() {
    const currentValue = this.refs.searchPageInput.value.trim();
    const urlParams = new URLSearchParams(window.location.search);
    const searchTerms = urlParams.get("q") || "";

    // Show reset button only if there are search terms from URL (preloaded)
    // and the current input value matches those terms
    const shouldShowReset =
      searchTerms !== "" && currentValue === searchTerms.trim();

    if (this.refs.searchButton) {
      this.refs.searchButton.style.display = shouldShowReset ? "none" : "flex";
    }

    if (this.refs.resetButton) {
      this.refs.resetButton.style.display = shouldShowReset ? "flex" : "none";
    }
  }
}

if (!customElements.get("search-page-input-component")) {
  customElements.define(
    "search-page-input-component",
    SearchPageInputComponent
  );
}
